import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../component/Navbar/Navbar";
import Footer from "../../component/Footer/Footer";
import heroImg from "./Assets/fullPicture.png";
import { useSmartDownload } from "../../hooks/useSmartDownload";
import { useNavigate } from "react-router-dom";
import { FaMicrophone } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import ArticleSidebar from "./ArticleSidebar";
import WhyDictateSection from "./WhyDictateSection";
import HowItWorksSection from "./HowItWorksSection";
import VoiceToTextTrustSection from "./VoiceToTextTrustSection";
import SpeechToTextExtensionSection from "./SpeechToTextExtensionSection";
import ComparisonSection from "./ComparisonSection";
import WhoUsesItSection from "./WhoUsesItSection";
import FAQSection from "./FAQSection";
import StartDictatingSection from "./StartDictatingSection";
import { ARTICLE_SECTIONS, SCROLL_OFFSET } from "./sections";

const VoiceTypingForGoogleDocs = () => {
  const smartDownload = useSmartDownload();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(ARTICLE_SECTIONS[0].id);

  const handleScrollTo = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    setActiveSection(id);
    const top =
      element.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
  };

  // Scroll spy: highlight the last section whose top has passed the probe line.
  useEffect(() => {
    const handleScroll = () => {
      const probe = window.scrollY + SCROLL_OFFSET + 40;
      let current = null;

      ARTICLE_SECTIONS.forEach((section) => {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top + window.scrollY <= probe) {
          current = section.id;
        }
      });

      if (current) setActiveSection(current);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Helmet>
        <title>Voice Typing for Google Docs | Zero Typing AI</title>
        <meta
          name="description"
          content="Fast, accurate AI voice typing for Google Docs. Speak naturally without punctuation commands and get instant, clean dictation."
        />
        <link
          rel="canonical"
          href="https://zerotyping.ai/voice-typing-for-google-docs"
        />
      </Helmet>

      <div className="relative overflow-hidden w-full bg-[#C9E9FC] border-b-[1.25px] border-[#E6ECF5] font-inter min-h-[774px]">
        {/* Floating colour glow - same orbiting-circle treatment as the Home hero */}
        <div
          className="pointer-events-none absolute inset-0 z-0 hidden md:block"
          aria-hidden="true"
        >
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 1440 774"
            preserveAspectRatio="none"
          >
            <path
              id="gdocsOrbitPath"
              d="M0,0 H1440 V774 H0 Z"
              fill="none"
              stroke="transparent"
            />

            <circle r="300" fill="url(#gdocsGlow)" filter="url(#gdocsBlur)">
              <animateMotion repeatCount="indefinite" dur="20s" rotate="auto">
                <mpath href="#gdocsOrbitPath" />
              </animateMotion>
            </circle>

            <circle r="300" fill="url(#gdocsGlow)" filter="url(#gdocsBlur)">
              <animateMotion
                repeatCount="indefinite"
                dur="20s"
                rotate="auto"
                keyPoints="1;0"
                keyTimes="0;1"
              >
                <mpath href="#gdocsOrbitPath" />
              </animateMotion>
            </circle>

            <defs>
              <radialGradient id="gdocsGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#3AB1F6" stopOpacity=".9" />
                <stop offset="100%" stopColor="#3AB1F6" stopOpacity="0" />
              </radialGradient>
              <filter
                id="gdocsBlur"
                x="-100%"
                y="-100%"
                width="300%"
                height="300%"
              >
                <feGaussianBlur in="SourceGraphic" stdDeviation="20" />
              </filter>
            </defs>
          </svg>
        </div>

        {/* Navigation Bar */}
        <Navbar />

        {/* Hero Section Container */}
        <div
          id="overview"
          className="max-w-[1440px] mx-auto pt-28 md:pt-36 pb-16 px-6 md:px-12 lg:px-20 relative z-10"
        >
          <div className="flex flex-col lg:flex-row items-start justify-between gap-12 lg:gap-14">
            {/* Left Content Side */}
            <div className="w-full lg:w-[645px] flex flex-col items-start text-left gap-6">
              <h1 className="text-[#0A0D16] text-[36px] sm:text-[44px] lg:text-[50px] font-bold leading-[1.2] lg:leading-[70px] tracking-[-1.35px] font-jakarta">
                Voice Typing for Google Docs That Actually Keeps Up With You
              </h1>

              <div className="flex flex-col gap-[30px] w-full max-w-[642px] font-inter text-[16px] font-normal leading-[29.6px] tracking-[0px] text-[#0A0D16B2]">
                <p>
                  Google Docs has a built-in voice typing tool, but anyone who has used it for more than a few sentences knows the problem. It drops punctuation, mishears technical terms, and stalls out the moment your internet connection hiccups.
                </p>
                <p>
                  Zero Typing AI is a voice typing application that runs anywhere on your phone and desktop. No stopping to say &quot;period&quot; or &quot;new paragraph.&quot; You talk, and the words show up ready to send.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="w-full flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-3">
                {/* Button 1: Try Zero Typing Free */}
                <button
                  type="button"
                  onClick={() => navigate("/demo")}
                  className="bg-white hover:bg-slate-50 text-[#3AB1F6] font-semibold text-[15px] sm:text-[16px] px-6 py-3.5 rounded-full flex items-center gap-3 shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 cursor-pointer"
                >
                  <span className="w-8 h-8 rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] flex items-center justify-center text-white shrink-0">
                    <FaMicrophone className="w-4 h-4" />
                  </span>
                  <span>Try Zero Typing Free</span>
                </button>

                {/* Button 2: Download Zero Typing */}
                <button
                  type="button"
                  onClick={() => smartDownload("google_docs_hero")}
                  className="bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#33A2E5] hover:to-[#386FE8] text-white font-semibold text-[15px] sm:text-[16px] px-6 py-3.5 rounded-full flex items-center gap-3 shadow-[0_4px_18px_rgba(58,177,246,0.35)] hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 cursor-pointer"
                >
                  <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#3AB1F6] shrink-0">
                    <FiDownload className="w-4 h-4" />
                  </span>
                  <span>Download Zero Typing</span>
                </button>
              </div>
            </div>

            {/* Right Side Container (starts in parallel with left side) */}
            <div className="w-full lg:w-[546px] flex flex-col items-center lg:items-start shrink-0">
              {/* Single composite visual: mock window, divider and stats are all
                  baked into the PNG, so no card frame or shadow is added here. */}
              <img
                src={heroImg}
                width={1147}
                height={1011}
                alt="Voice typing into Google Docs with Zero Typing AI: 3× faster than typing, 99% uptime, 10k+ active users"
                className="animate-float block w-full max-w-[546px] h-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Article body: sticky section nav + content column */}
      <div className="w-full bg-white font-inter">
        {/* No bottom padding: the CTA panel below supplies the gap */}
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-[101px] pt-12 md:pt-16 lg:pt-24">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-[63px]">
            <ArticleSidebar
              activeSection={activeSection}
              onSelect={handleScrollTo}
            />

            <div className="w-full lg:max-w-[815.58px] min-w-0 flex flex-col gap-[64px] md:gap-[80px] lg:gap-[96px]">
              <WhyDictateSection />
              <HowItWorksSection />
              <VoiceToTextTrustSection />
              <SpeechToTextExtensionSection />
              <ComparisonSection />
              <WhoUsesItSection />
              <FAQSection />
            </div>
          </div>
        </div>
      </div>

      {/* Full-width closing CTA - sits below both the sidebar and the article */}
      <StartDictatingSection />

      <Footer />
    </>
  );
};

export default VoiceTypingForGoogleDocs;
